import { useState } from 'react';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { api } from '@/lib/api';
import { fetchCurrentUser } from '@/lib/auth';
import type { ApiError } from '@/lib/api';

interface PasswordModalProps {
  open: boolean;
  onClose: () => void;
  hasCurrentPassword: boolean;
}

export function PasswordModal({ open, onClose, hasCurrentPassword }: PasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    // Reset form state
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (hasCurrentPassword && !currentPassword) {
      setError('Current password is required');
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post('/auth/change-password', {
        currentPassword: hasCurrentPassword ? currentPassword : undefined,
        newPassword,
      });

      // Refresh user data to update hasPassword status
      await fetchCurrentUser();

      setSuccess(true);

      // Close modal after a short delay
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to update password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={hasCurrentPassword ? 'Change Password' : 'Set Password'}
      size="sm"
    >
      {success ? (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <p className="text-lg font-medium text-foreground">
            Password {hasCurrentPassword ? 'changed' : 'set'} successfully!
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {!hasCurrentPassword && (
            <div className="rounded border border-primary/30 bg-primary/10 p-3">
              <p className="text-sm text-foreground">
                You're setting a password for the first time. This will allow you to sign in with
                email and password in addition to your social accounts.
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-error/10 p-3 text-sm text-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {hasCurrentPassword && (
            <Input
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
              leftIcon={<Lock className="h-4 w-4" />}
              required
              autoFocus
            />
          )}

          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
            leftIcon={<Lock className="h-4 w-4" />}
            required
            autoFocus={!hasCurrentPassword}
          />

          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter your new password"
            leftIcon={<Lock className="h-4 w-4" />}
            required
          />

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              {hasCurrentPassword ? 'Change Password' : 'Set Password'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
